import { zodResolver } from '@hookform/resolvers/zod'
import { PlusIcon } from '@radix-ui/react-icons'
import { useMutation } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { Trash2Icon } from 'lucide-react'
import { useFieldArray, useForm, useFormContext } from 'react-hook-form'
import { toast } from 'sonner'
import { z } from 'zod'

import {
  EducationLevel,
  EmploymentType,
  GetUserEducationQuery,
  GetUserExperienceQuery,
  LocationType,
  UpsertUserEducationRequest,
  UpsertUserExperienceRequest,
} from '@/_gql/graphql'

import { gqlFetch } from '@/services/graphql/fetcher'
import {
  EDUCATION_LEVEL_OPTIONS,
  EMPLOYMENT_STATUS_OPTIONS,
} from '@/services/user/constant'
import {
  GqlUserEducationUpsert,
  GqlUserExperienceUpsert,
} from '@/services/user/gql'
import {
  queryUserEducation,
  queryUserExperience,
  queryUserSubmissionProgress,
} from '@/services/user/query'

import { Button } from '@/components/ui/button'
import { CardTitle } from '@/components/ui/card'
import { Checkbox } from '@/components/ui/checkbox'
import { Form, FormField } from '@/components/ui/form'
import { FormFieldDatePicker } from '@/components/ui/form-field/date-picker'
import { FormFieldset } from '@/components/ui/form-field/fieldset'
import { FormFieldSelect } from '@/components/ui/form-field/select'
import { Input } from '@/components/ui/input'
import { InputMonthPicker, YearMonth } from '@/components/ui/input-month-picker'
import { Textarea } from '@/components/ui/textarea'

import {
  formatDateOnly,
  formatDateTimeServer,
  normalizeEnum,
} from '@/lib/utils/utils'

import { queryClient } from '@/lib/configs/tanstack-query'
import { CardProfile } from '../digishoku/profile/-components/card-profile'

export const Route = createFileRoute('/_user/profile/experience')({
  loader: async ({ context: { queryClient: qc } }) => {
    const [experience] = await Promise.all([
      qc.fetchQuery(queryUserExperience()),
    ])

    return {
      experience,
      async refetch() {
        await qc.refetchQueries(queryUserExperience())
      },
    }
  },
  component: RouteComponent,
})

function RouteComponent() {
  const { experience, refetch } = Route.useLoaderData()
  const navigate = Route.useNavigate()
  const form = useForm<FormDto>({
    resolver: zodResolver(FormDto),
    defaultValues: getDefaultValues(experience),
  })

  const experiencesFields = useFieldArray({
    control: form.control,
    name: 'experiences',
    keyName: 'kId',
  })

  const { mutateAsync: mutateExperienceAsync } = useMutation({
    mutationFn: async (data: FormDto) => {
      await gqlFetch({
        query: GqlUserExperienceUpsert,
        variables: { request: { request: formDtoToPayloadExperiences(data) } },
      })
      await refetch()
      // await queryClient.invalidateQueries(queryUserSubmissionProgress())
      navigate({ to: '/profile/licences' })
      return true
    },
  })

  const handleOnSubmit = form.handleSubmit((data) => {
    const promises = []

    if (data.experiences.length > 0) {
      promises.push(mutateExperienceAsync(data))
    }

    if (promises.length === 0) {
      toast.error('No data to submit')
      return
    }

    navigate({ to: '/profile/licences' })

    toast.promise(Promise.all(promises), {
      loading: 'Menyimpan informasi pekerjaan',
      success: 'Informasi pekerjaan berhasil disimpan',
      error: 'Terjadi kesalahan saat menyimpan informasi',
    })
  }, console.error)

  const showRemoveExperience = experiencesFields.fields.length > 1

  return (
    <>
      <Form {...form}>
        <form onSubmit={handleOnSubmit}>
          <CardProfile title="Riwayat Pekerjaan" isRequired={true}>
            <CardProfile.Content className="flex flex-col">
              {experiencesFields.fields.map((field, idx) => {
                return (
                  <ExperienceFormItem
                    key={field.kId}
                    idx={idx}
                    showRemove={showRemoveExperience}
                    onRemove={() => {
                      experiencesFields.remove(idx)
                    }}
                  />
                )
              })}
              <div className="col-span-3">
                <Button
                  className="w-full rounded-xl border-primary-yes text-primary-yes md:w-auto"
                  variant="outline"
                  type="button"
                  onClick={() =>
                    experiencesFields.append(defaultItemExperiences())
                  }
                >
                  <PlusIcon />
                  Tambah Riwayat Pekerjaan
                </Button>
              </div>
            </CardProfile.Content>
            <CardProfile.Footer>
              <CardProfile.SubmissionButton />
            </CardProfile.Footer>
          </CardProfile>
        </form>
      </Form>
    </>
  )
}

interface FormDto extends z.infer<typeof FormDto> {}
const FormDto = z.object({
  experiences: z.array(
    z.object({
      id: z.coerce.number().optional(),
      title: z.string().min(1),
      company_name: z.string().min(1),
      description: z.string().optional(),
      start_date: z.coerce.date(),
      end_date: z.coerce.date().nullable(),
      created_at: z.string().optional(),
      location: z.string().optional(),
      location_type: z.string().optional(),
      employment_type: z.nativeEnum(EmploymentType, {
        required_error: 'Status karyawan tidak boleh kosong',
        invalid_type_error: 'Status karyawan tidak valid',
        message: 'Status karyawan tidak valid/tidak boleh kosong',
      }),
    })
  ),
})

interface ExperienceFormItemProps {
  idx: number
  showRemove: boolean
  onRemove?: () => void
}

function ExperienceFormItem(props: ExperienceFormItemProps) {
  const { idx, showRemove, onRemove } = props
  const form = useFormContext<FormDto>()

  return (
    <div className="relative flex grid-cols-2 flex-col gap-x-2 gap-y-6 rounded-md border border-border p-4 py-6 md:grid md:grid-cols-6">
      <div className="absolute right-0 top-0">
        <Button variant="ghost" size="icon" onClick={onRemove} type="button">
          <Trash2Icon className="size-16 text-primary-yes" />
        </Button>
      </div>

      <FormField
        control={form.control}
        name={`experiences.${idx}.title`}
        render={({ field }) => (
          <FormFieldset label="Posisi" className="col-span-3">
            <Input placeholder="Masukkan Posisi" {...field} />
          </FormFieldset>
        )}
      />

      <FormField
        control={form.control}
        name={`experiences.${idx}.company_name`}
        render={({ field }) => (
          <FormFieldset label="Nama perusahaan" className="col-span-3">
            <Input placeholder="Masukkan Nama perusahaan" {...field} />
          </FormFieldset>
        )}
      />

      <div className="col-span-3 space-y-2">
        <FormField
          control={form.control}
          name={`experiences.${idx}.start_date`}
          render={({ field }) => (
            <FormFieldset label="Tanggal mulai" noFormControl>
              <InputMonthPicker
                asForm
                value={field.value}
                onChange={field.onChange}
                placeholder="Pilih Tanggal"
              />
            </FormFieldset>
          )}
        />
        <div className="flex items-center space-x-2">
          <Checkbox
            id={`experiences-${idx}-end-date-checkbox`}
            checked={form.watch(`experiences.${idx}.end_date`) === null}
            onCheckedChange={(checked) => {
              form.setValue(
                `experiences.${idx}.end_date`,
                checked ? null : undefined!
              )
            }}
          />
          <label
            htmlFor={`experiences-${idx}-end-date-checkbox`}
            className="text-sm leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
          >
            Saya masih bekerja di tempat ini
          </label>
        </div>
      </div>

      <FormField
        control={form.control}
        name={`experiences.${idx}.end_date`}
        render={({ field }) => (
          <FormFieldset
            label="Tanggal berakhir"
            className="col-span-3"
            noFormControl
          >
            <InputMonthPicker
              asForm
              value={field.value || undefined}
              onChange={field.onChange}
              placeholder="Pilih Tanggal"
            />
          </FormFieldset>
        )}
      />

      <FormField
        control={form.control}
        name={`experiences.${idx}.employment_type`}
        render={({ field }) => (
          <FormFieldset label="Status karyawan" className="col-span-2">
            <FormFieldSelect
              placeholder="Pilih status karyawan"
              items={EMPLOYMENT_STATUS_OPTIONS}
              value={field.value}
              onValueChange={field.onChange}
            />
          </FormFieldset>
        )}
      />
      <FormField
        control={form.control}
        name={`experiences.${idx}.description`}
        render={({ field }) => (
          <FormFieldset label="Deskripsi Pekerjaan" className="col-span-6">
            <Textarea
              placeholder="Jelaskan tugas dan tanggung jawab Anda..."
              rows={3}
              {...field}
            />
          </FormFieldset>
        )}
      />
    </div>
  )
}

type EducationData = GetUserEducationQuery['getUserEducation']['data'] | null
type ExperienceData = GetUserExperienceQuery['getUserExperience']['data'] | null

function getDefaultValues(initialExperiences?: ExperienceData): FormDto {
  const data: FormDto = {
    experiences: (initialExperiences ?? []).map((x) => ({
      ...x,
      employment_type:
        (x.employment_type?.toUpperCase() as EmploymentType) ?? '',
      description: (x as any)?.description || '',
      start_date: x.start_date ? new Date(x.start_date) : undefined!,
      end_date: x.end_date ? new Date(x.end_date) : null,
      location_type: (x.location_type?.toUpperCase() as LocationType) ?? '',
      created_at: x.created_at || undefined!,
      location: '',
    })),
  }

  if (!data.experiences.length) data.experiences.push(defaultItemExperiences())

  return data
}

function defaultItemExperiences(): FormDto['experiences'][number] {
  return {
    id: undefined,
    title: '',
    company_name: '',
    description: '',
    start_date: undefined!,
    end_date: undefined!,
    created_at: undefined!,
    employment_type: undefined!,
    location: '',
    location_type: undefined!,
  }
}

function formDtoToPayloadExperiences(
  data: FormDto
): UpsertUserExperienceRequest['request'] {
  return data.experiences.map((x) => ({
    id: x.id,
    title: x.title,
    company_name: x.company_name,
    description: x.description,
    start_date: formatDateOnly(x.start_date),
    end_date: x.end_date ? formatDateOnly(x.end_date) : null,
    created_at: x.created_at ? formatDateTimeServer(x.created_at) : undefined,
    location: x.location ?? '',
    employment_type: x.employment_type,
    // location_type: x.location_type ?? undefined
  }))
}
